import {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useEffect,
	useRef,
	useState,
} from "react";

type PlaybackContextType = {
	isPlaying: boolean;
	currentTrack: string | null;
	currentTime: number;
	duration: number;
	togglePlayPause: () => void;
	playTrack: (track: string) => void;
	setCurrentTime: (time: number) => void;
	setDuration: (duration: number) => void;
	setTrack: (track: string) => void;
	audioRef: React.RefObject<HTMLAudioElement | null>;
};

const PlaybackContext = createContext<PlaybackContextType | undefined>(undefined);

export function PlaybackProvider({ children }: { children: ReactNode }) {
	const [isPlaying, setIsPlaying] = useState(false);
	const [currentTrack, setCurrentTrack] = useState<string | null>(null);
	const [currentTime, setCurrentTime] = useState(0);
	const [duration, setDuration] = useState(0);
	const audioRef = useRef<HTMLAudioElement>(null);

	const togglePlayPause = useCallback(() => {
		if (audioRef.current) {
			if (isPlaying) {
				audioRef.current.pause();
			} else {
				audioRef.current.play();
			}
			setIsPlaying(!isPlaying);
		}
	}, [isPlaying]);

	const playTrack = useCallback((track: string) => {
		setCurrentTrack(track);
		setIsPlaying(true);
		setCurrentTime(0);
		if (audioRef.current) {
			audioRef.current.src = track;
			audioRef.current.play();
		}
	}, []);

	useEffect(() => {
		const handleGlobalKeyDown = (e: KeyboardEvent) => {
			if (e.key === " " && e.target === document.body) {
				e.preventDefault();
				togglePlayPause();
			}
		};

		window.addEventListener("keydown", handleGlobalKeyDown);
		return () => window.removeEventListener("keydown", handleGlobalKeyDown);
	}, [togglePlayPause]);

	return (
		<PlaybackContext.Provider
			value={{
				isPlaying,
				currentTrack,
				currentTime,
				duration,
				togglePlayPause,
				playTrack,
				setCurrentTime,
				setDuration,
				audioRef,
				setTrack: setCurrentTrack,
			}}
		>
			{children}
			<audio className="hidden" ref={audioRef} />
		</PlaybackContext.Provider>
	);
}

export function usePlayback() {
	const context = useContext(PlaybackContext);

	if (context === undefined) {
		throw new Error("usePlayback must be used within a PlaybackProvider");
	}

	return context;
}
